import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import {
  IAdmin,
  IAdminRepresentative,
  IAgentRepresentative,
  IBrokerRepresentative,
  InsurerRepresentative,
  IPaginatedResponse,
  IPartnerRepresentative,
  IRepresentative,
  ModuleConfig,
  SystemUserRole,
} from '@inshuwa-client/models';
import { HttpClient } from '@angular/common/http';
import { baseUrl } from 'libs/core/src/lib/configs/baseUrl';
import { SharedAuthService } from 'libs/core/src/lib/services/auth.service';
import { sessionStore } from '../utils/session-store';

@Injectable({
  providedIn: 'root',
})
export class RepresentativeService {
  readonly http = inject(HttpClient);
  readonly sharedAuthService = inject(SharedAuthService);
  getLoggedInBrokerRepresentative(): IBrokerRepresentative {
    return JSON.parse(
      `${localStorage.getItem(SystemUserRole.BrokerRepresentative)}`,
    );
  }

  getAgentRepresentativeById(id: number): Observable<IAgentRepresentative> {
    return this.http
      .get<IAgentRepresentative>(`${baseUrl()}/agencies/representatives/${id}`)
      .pipe(
        switchMap((value) => {
          return this.sharedAuthService
            .getMyPermissions()
            .pipe(map((_) => value));
        }),
      );
  }

  // Used to take an `insurer` name, forwarded as a query param and sourced from the build's
  // environment.name. BrokerService.GetRepresentative accepts it and never reads it - the parameter
  // is dead on the server - so this drops it rather than finding a runtime value to feed it.
  getRepresentativeBrokerById(id: number): Observable<IBrokerRepresentative> {
    return this.http
      .get<IBrokerRepresentative>(`${baseUrl()}/brokers/representatives/${id}`)
      .pipe(
        switchMap((value) => {
          return this.sharedAuthService
            .getMyPermissions()
            .pipe(map((_) => value));
        }),
      );
  }
  getCurrentAdmin(): Observable<IAdmin> {
    return this.http.get<IAdmin>(`${baseUrl()}/admin`).pipe(
      switchMap((value) => {
        return this.sharedAuthService
          .getMyPermissions()
          .pipe(map((_) => value));
      }),
    );
  }

  getCurrentPartner(id: number): Observable<IPartnerRepresentative> {
    return this.http
      .get<IPartnerRepresentative>(
        `${baseUrl()}/partners/representatives/${id}`,
      )
      .pipe(
        switchMap((value) => {
          return this.sharedAuthService
            .getMyPermissions()
            .pipe(map((_) => value));
        }),
      );
  }
  getInsurerRepresentativeById(id: number): Observable<InsurerRepresentative> {
    return this.http
      .get<InsurerRepresentative>(`${baseUrl()}/insurers/representatives/${id}`)
      .pipe(
        switchMap((value) => {
          return this.sharedAuthService
            .getMyPermissions()
            .pipe(map((_) => value));
        }),
      );
  }

  getLoggedInInsurerRepresentative(): InsurerRepresentative {
    return JSON.parse(
      `${localStorage.getItem(SystemUserRole.InsurerRepresentative)}`,
    );
  }

  // True when the currently logged-in user is an insurer's own staff member,
  // even while issuing/quoting on behalf of an agent, broker, or partner.
  isInsurerRepresentative(): boolean {
    return !!this.getLoggedInInsurerRepresentative()?.id;
  }

  isAdmin(): boolean {
    return !!this.getLoggedInAdminRepresentative()?.adminId;
  }

  getLoggedInPartnerRepresentative(): IPartnerRepresentative {
    return JSON.parse(
      `${sessionStore.getItem(SystemUserRole.PartnerRepresentative)}`,
    );
  }
  getLoggedInAgentRepresentative(): IAgentRepresentative {
    return JSON.parse(
      `${localStorage.getItem(SystemUserRole.AgentRepresentative)}`,
    );
  }

  getLoggedInAdminRepresentative(): IAdminRepresentative {
    return JSON.parse(`${localStorage.getItem(SystemUserRole.admin)}`);
  }

  getBranch(config: ModuleConfig) {
    switch (config.mode) {
      case 'insurer':
        return this.getLoggedInInsurerRepresentative().branch ?? null;
      case 'single_broker':
        return this.getLoggedInBrokerRepresentative().branch ?? null;
      case 'agent':
        return this.getLoggedInAgentRepresentative().branch ?? null;
      case 'partner':
        return this.getLoggedInPartnerRepresentative().branch ?? null;
      default:
        return null;
    }
  }
}
